import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";
import { Section } from "./Section";
import { GpuRack, TutorBot } from "../illustrations/Scenes";

// Six stages, read left to right like a mindmap rather than down a page of
// paragraphs. Each one is a headline and a single line — the detail belongs in
// the first conversation, not here.
const STEPS = [
  {
    n: "01",
    title: "We sit down and talk",
    line: "An hour or two on where you are, where you're going, and what your week really holds.",
  },
  {
    n: "02",
    title: "We write the plan together",
    line: "Bound to milestones, never dates. Finish one and the next opens.",
  },
  {
    n: "03",
    title: "We teach you, one to one",
    line: "Live sessions at your pace. Nobody moves you on before you're ready.",
  },
  {
    n: "04",
    title: "You practise on what we build for you",
    line: "Study books, assignments and assessments written for your plan and marked by your tutor.",
  },
  {
    n: "05",
    title: "You build on real infrastructure",
    line: "AWS credits and premium Colab, so you train and deploy on real GPUs.",
  },
  {
    n: "06",
    title: "A job, or your own product",
    line: "Interviews and offers, or your own thing shipped. We stay until you're there.",
  },
];

// What you get for the whole journey, rather than at one step of it.
const ALWAYS = [
  { k: "Your own learning platform", v: "Everything in one place, built for this" },
  { k: "Lifetime access", v: "Nothing expires, ever" },
  { k: "Every class recorded", v: "Rewatch any session, any time" },
  { k: "An AI tutor, always awake", v: "For the 2am questions between sessions" },
  { k: "Books, assignments, assessments", v: "Written for your plan, not the shelf" },
  { k: "AWS credits & premium Colab", v: "Real GPUs, real deployments" },
];

export const Journey = () => {
  const reduced = useReducedMotion();

  return (
    <Section
      id="how-it-works"
      align="left"
      eyebrow="The candidate journey"
      title="How your journey actually unfolds"
      lead="Six stages, at your pace. Everything below the line stays with you the whole way through."
    >
      <motion.div variants={stagger(0.07)} {...revealProps(reduced)}>
        {/* The path, read across */}
        <Grid container spacing={{ xs: 3, md: 2.5 }}>
          {STEPS.map((s) => (
            <Grid item xs={12} sm={6} md={2} key={s.n}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <Box sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        backgroundColor: palette.primary,
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      aria-hidden="true"
                      sx={{
                        flex: 1,
                        height: "2px",
                        backgroundColor: palette.border,
                      }}
                    />
                  </Box>
                  <Typography
                    aria-hidden="true"
                    sx={{
                      color: palette.primary,
                      fontWeight: 700,
                      fontSize: 12.5,
                      letterSpacing: "0.06em",
                      mb: 1,
                    }}
                  >
                    {s.n}
                  </Typography>
                  <Typography
                    component="h3"
                    sx={{
                      color: palette.text,
                      fontWeight: 700,
                      fontSize: { xs: 17, md: 17.5 },
                      letterSpacing: "-0.02em",
                      lineHeight: 1.3,
                      mb: 1,
                    }}
                  >
                    {s.title}
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      color: palette.textMuted,
                      fontSize: 14,
                      lineHeight: 1.55,
                    }}
                  >
                    {s.line}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Constant throughout, not a stage */}
        <motion.div variants={fadeUp}>
          <Box
            sx={{
              mt: { xs: 6, md: 8 },
              pt: { xs: 4, md: 5 },
              borderTop: `1px solid ${palette.border}`,
            }}
          >
            <Typography
              component="h3"
              sx={{
                color: palette.text,
                fontWeight: 700,
                fontSize: { xs: 19, md: 21 },
                letterSpacing: "-0.02em",
                mb: 0.75,
              }}
            >
              Yours for the whole journey
            </Typography>
            <Typography
              component="p"
              sx={{ color: palette.textMuted, fontSize: 15.5, mb: 3.5 }}
            >
              Not per stage, not an upgrade. Included from day one.
            </Typography>

            <Grid container spacing={{ xs: 4, md: 5 }} alignItems="center">
              <Grid item xs={12} md={7}>
                <Grid container spacing={{ xs: 2.5, md: 3 }}>
                  {ALWAYS.map((a) => (
                    <Grid item xs={12} sm={6} key={a.k}>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 6,
                        height: 6,
                        mt: "8px",
                        flexShrink: 0,
                        borderRadius: "50%",
                        backgroundColor: palette.primary,
                      }}
                    />
                    <Box>
                      <Typography
                        component="p"
                        sx={{
                          color: palette.text,
                          fontWeight: 700,
                          fontSize: 15.5,
                          lineHeight: 1.4,
                        }}
                      >
                        {a.k}
                      </Typography>
                      <Typography
                        component="p"
                        sx={{ color: palette.textMuted, fontSize: 14, lineHeight: 1.5 }}
                      >
                        {a.v}
                      </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
                  <Box sx={{ flex: 1.2 }}><GpuRack /></Box>
                  <Box sx={{ flex: 1 }}><TutorBot /></Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default Journey;
